import { api } from 'api';
import NoticeIcon from 'assets/icons/notice-yellow-icon.svg';
import InformPopup from 'components/common/InformPopup';
import {
  ADD_TRANSACTION_PAYMENT_METHOD_NAME,
  ADD_TRANSACTION_PAYOUT_METHOD_NAME,
  ADD_TRANSACTION_RECEIVER_GET_NAME,
  ADD_TRANSACTION_YOU_SEND_ANOTHER_NAME,
  ADD_TRANSACTION_YOU_SEND_NAME,
  FUNC_DEFAULT,
  PROMOTION,
  VOUCHER,
} from 'constants';
// Imported from the subpath rather than the bare 'constants' barrel: under the
// Jest/jsdom module resolver the bare specifier collides with the Node core
// `constants` builtin and yields undefined, which would silently disable the
// expired-discount check below. The subpath resolves to src/constants/discount
// in both webpack and Jest.
import { DISCOUNT_EXPIRED } from 'constants/discount';
import useCalculatorRoundingStore from 'features/calculatorRounding/hooks/useCalculatorRoundingStore';
import { filterDiscountsAvailable } from 'helpers';
import { checkMaintenance, checkSystemError } from 'helpers/maintenance';
import { isArray, isEmpty, isFunction } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { dispatchStore, stateStore } from 'store';
import {
  SET_ADD_TRANSACTION_INFORMATION,
  SET_SHOW_LOADING,
} from 'store/action';
import { arrayUtils, numberUtils } from 'utils';

const DISCOUNT_CONDITIONS_EXCLUDED_ERROR_CODES = ['E003', 'E011', 'V002'];

export const isDiscountConditionsErrorCodeExcluded = (errorCode) =>
  DISCOUNT_CONDITIONS_EXCLUDED_ERROR_CODES.includes(errorCode);

// Backend discount validation error codes (base/promotion/voucher conditions).
// Non-discount failures (payment, OTP, network, server) fall outside this set
// and must be handled by the generic transaction error flow, not the discount
// failure popup.
const DISCOUNT_ERROR_CODES = [
  'E001',
  'E002',
  'E003',
  'E004',
  'E005',
  'E006',
  'E007',
  'E008',
  'E009',
  'E010',
  'E011',
  'E012',
  'E013',
  'E014',
  'P001',
  'P002',
  'P003',
  'V001',
  'V002',
];

export const isDiscountErrorCode = (errorCode) =>
  DISCOUNT_ERROR_CODES.includes(errorCode);

export const isFailedDiscountConditionsViewable = ({
  failedDiscount,
  errorCode,
}) =>
  Boolean(failedDiscount) && !isDiscountConditionsErrorCodeExcluded(errorCode);

export const fetchAllDiscounts = async (token, cb) => {
  try {
    const { data } = await api.getAllDiscounts(token);

    if (data && !arrayUtils.isArrayEmpty(data)) {
      cb(data);
    }
  } catch (error) {
    checkMaintenance(error);
    console.error(error?.message);
  }
};

export const fetchAllEvents = async (token, cb) => {
  try {
    const { data } = await api.getAllEvents(token);

    if (data && !arrayUtils.isArrayEmpty(data)) {
      cb(data);
    }
  } catch (error) {
    checkMaintenance(error);
    console.error(error?.message);
  }
};

// Single source of truth for the transaction fee used in discount work. The
// /quote response (calculatorRoundingResponse.fee.feeAmount) is the reliable,
// amount-driven fee and is available once the calculator settles. It is NOT
// payment-method dependent. remittance.feeAmount lags it and can be stale/0, so
// reading that in the eligibility derive misclassified FLAT_FEE discounts (which
// gate on discountAmount <= feeAmount) as inactive at Step 2 even though the real
// fee already qualified them. Both the eligibility derive and the apply/validation
// call read this so a discount's availability and its apply attempt agree.
const getDiscountTxnFeeAmount = () =>
  Number(stateStore?.calculatorRoundingResponse?.fee?.feeAmount || 0);

export const deriveTransactionDiscountLists = ({
  discounts = [],
  countryCode,
  payoutMethod,
  currencyCode,
  isOnlyCurrency,
  sendAmount,
  receiverAmount,
  feeAmount,
  cityId,
  paymentMethod = '',
}) => {
  const discountList = isArray(discounts) ? discounts : [];

  const eligibleDiscountsAvailable =
    filterDiscountsAvailable({
      discounts: discountList,
      countryCode,
      deliveryMethod: payoutMethod,
      currencyCode,
      isOnlyCurrency,
      sendAmount,
      receiverAmount,
      feeAmount,
      cityId,
      paymentMethod,
    }) || [];

  // Only a HARD-invalidated discount stays out of the available section
  // regardless of eligibility. A discount becomes hard-invalid when an applied
  // discount fails transaction creation at Step 4 with a discount error code
  // (e.g. E003): markDiscountApplyFailed({ hardInvalid: true }) and
  // stampHardInvalidDiscounts both mark it with `hardInvalid: true`, and it must
  // remain inactive for the rest of the send-money flow. Soft Step-2 eligibility
  // failures (an applyErrorCode with no hardInvalid) are NOT locked: a re-derive
  // on an amount/currency change re-evaluates them and they return to available
  // when they are eligible under the new context.
  const discountsAvailable = eligibleDiscountsAvailable.filter(
    (discount) => !discount?.hardInvalid,
  );

  const discountsInactive = arrayUtils.filterFromAnotherArrayByProp(
    discountList,
    discountsAvailable,
    'code',
  );

  return {
    discountsAvailable,
    discountsInactive,
  };
};

// Identity of the SERVER fetch. `api.getDiscountsActive(token)` takes only the
// token — it sends no currency/amount and returns the full active list, which is
// then filtered client-side (deriveTransactionDiscountLists). The active list is
// therefore fetched once per send-money flow lifecycle and refetched only when
// Step-1 context changes; a Step-2 currency change must reuse the cached list. So
// the fetch key is Step-1 receiver/payout context only: countryCode, payoutMethod,
// isOnlyCurrency, cityId, receiverId. currencyCode is deliberately EXCLUDED — it is
// a Step-2 field, so a currency switch re-filters the cached list locally (via the
// derive key) instead of refetching. receiverId is part of the key because two
// different receivers in the same city share every other field yet can have
// different eligibility; including it means a return to Step 2 refetches only when
// the receiver/payout context actually changed — a same-city receiver swap changes
// the key (fresh fetch), while returning with the same receiver reuses the cached
// list (no refetch). It also excludes amounts, so editing amounts does NOT trigger
// a refetch either. See buildTransactionDiscountDeriveKey for the currency- and
// amount-sensitive client re-filter trigger.
export const buildTransactionDiscountFetchKey = ({
  countryCode,
  payoutMethod,
  isOnlyCurrency,
  cityId,
  receiverId,
}) =>
  [
    countryCode,
    payoutMethod,
    isOnlyCurrency,
    cityId,
    receiverId,
  ].join('|');

// Identity of the client-side FILTER. Eligibility depends on the amounts, so when
// this key changes (but the fetch key does not) the stored raw active list is
// re-filtered locally without a server call. Includes the fetch-context fields so
// it is fully specific, plus the amount inputs to filterDiscountsAvailable. Fee is
// deliberately excluded from the key: it is recalculated from the amounts, so the
// amount change already triggers the re-derive, and the current fee is read from
// the store at derive time (same source the fetch uses).
export const buildTransactionDiscountDeriveKey = ({
  countryCode,
  payoutMethod,
  currencyCode,
  isOnlyCurrency,
  cityId,
  receiverId,
  sendAmount,
  receiverAmount,
}) =>
  // Listed inline (not composed from the fetch key) because currencyCode drives
  // local re-filtering but is intentionally absent from the fetch key. A Step-2
  // currency switch must change the derive key (re-filter the cached list for the
  // new currency's eligibility/thresholds) without changing the fetch key (no
  // refetch).
  [
    countryCode,
    payoutMethod,
    currencyCode,
    isOnlyCurrency,
    cityId,
    receiverId,
    sendAmount,
    receiverAmount,
  ].join('|');

export const getTransactionDiscountRecoveryContext = ({
  countryCode,
  payoutMethod,
  currencyCode,
  isOnlyCurrency,
  sendAmount,
  sendAnotherAmount,
  receiverAmount,
  cityId,
  receiverId,
}) => {
  if (!countryCode) return null;
  if (!payoutMethod) return null;
  if (!currencyCode) return null;

  const sendTotalAmount =
    (Number(sendAmount || 0) || 0) + (Number(sendAnotherAmount || 0) || 0);
  const receiveAmount = Number(receiverAmount || 0) || 0;
  const discountFetchKey = buildTransactionDiscountFetchKey({
    countryCode,
    payoutMethod,
    currencyCode,
    isOnlyCurrency,
    cityId,
    receiverId,
  });
  const discountDeriveKey = buildTransactionDiscountDeriveKey({
    countryCode,
    payoutMethod,
    currencyCode,
    isOnlyCurrency,
    cityId,
    receiverId,
    sendAmount: sendTotalAmount,
    receiverAmount: receiveAmount,
  });

  return {
    cityId,
    countryCode,
    currencyCode,
    discountFetchKey,
    discountDeriveKey,
    isOnlyCurrency,
    receiverAmount: receiveAmount,
    sendAmount: sendTotalAmount,
  };
};

export const isTransactionDiscountRecoveryLoaded = ({
  discountRecoveryContext,
  isDiscountsLoaded,
  storedFetchKey,
}) => {
  const currentDiscountFetchKey =
    discountRecoveryContext?.discountFetchKey || '';

  return (
    isDiscountsLoaded &&
    Boolean(currentDiscountFetchKey) &&
    storedFetchKey === currentDiscountFetchKey
  );
};

export const getTransactionDiscountRecoveryFetchAction = ({
  discountRecoveryContext,
  activeDiscountFetchKey,
  activeDiscountDeriveKey,
  forceRefetch = false,
  paymentMethod,
  // Discount-recovery keys/list now live at the top level of
  // addTransactionInformation (siblings of paymentMethod) so step navigation
  // dispatches of `paymentMethod: information` no longer clobber them. Callers
  // pass them explicitly to keep this helper pure. `paymentMethod` is still read
  // only for discountDetails (the stored default-apply failure).
  storedFetchKey,
  storedDeriveKey,
  storedDiscountsActive,
}) => {
  const discountFetchKey = discountRecoveryContext?.discountFetchKey || '';
  const discountDeriveKey = discountRecoveryContext?.discountDeriveKey || '';

  const noAction = {
    discountFetchKey,
    discountDeriveKey,
    hasStoredDefaultApplyFailure: false,
    shouldFetch: false,
    shouldUseStoredDiscounts: false,
    shouldRederive: false,
  };

  if (!discountFetchKey) return noAction;

  const hasStoredDefaultApplyFailure = Boolean(
    paymentMethod?.discountDetails?.isDefault &&
    paymentMethod?.discountDetails?.applyErrorCode,
  );

  // Forced refresh (e.g. returning to Step 2 from Step 1, where receiver/payout
  // context may have changed in ways the fetch key does not capture, such as a
  // different receiver in the same city). Fetch once per mount even if the stored
  // key matches; the activeDiscountFetchKey guard stops it repeating after the
  // fetch lands.
  if (forceRefetch && activeDiscountFetchKey !== discountFetchKey) {
    return { ...noAction, hasStoredDefaultApplyFailure, shouldFetch: true };
  }

  const storedFetchMatches = storedFetchKey === discountFetchKey;
  const hasStoredActiveList = Array.isArray(storedDiscountsActive);

  // No cached server list for this receiver/payout context (context changed, or
  // never fetched): fetch the active list from the server, once per key per mount.
  // The fetch is amount-independent, so amount edits never reach this branch.
  if (!storedFetchMatches || !hasStoredActiveList) {
    if (activeDiscountFetchKey === discountFetchKey) {
      return { ...noAction, hasStoredDefaultApplyFailure };
    }
    return { ...noAction, hasStoredDefaultApplyFailure, shouldFetch: true };
  }

  // Cached server list exists for this context. If the stored filtered lists were
  // built at the current amounts, reuse them; otherwise re-filter the cached raw
  // list locally for the new amounts — no server call.
  const storedDeriveMatches = storedDeriveKey === discountDeriveKey;

  if (storedDeriveMatches) {
    if (
      activeDiscountDeriveKey === discountDeriveKey &&
      activeDiscountFetchKey === discountFetchKey
    ) {
      return { ...noAction, hasStoredDefaultApplyFailure };
    }
    return {
      ...noAction,
      hasStoredDefaultApplyFailure,
      shouldUseStoredDiscounts: true,
    };
  }

  if (activeDiscountDeriveKey === discountDeriveKey) {
    return { ...noAction, hasStoredDefaultApplyFailure };
  }
  return { ...noAction, hasStoredDefaultApplyFailure, shouldRederive: true };
};

// Re-filter the cached raw active list for the current amounts, entirely
// client-side (no server call). Used when only the amount-derive key changed.
// Mirrors the filtering fetchDiscountsAvailable applies, reading fee from the
// store the same way the fetch does.
export const refilterStoredTransactionDiscounts = ({
  discountRecoveryContext,
  rawDiscounts,
  payoutMethod,
  paymentMethod = '',
  feeAmount,
}) => {
  const discounts = Array.isArray(rawDiscounts) ? rawDiscounts : [];

  return deriveTransactionDiscountLists({
    discounts,
    countryCode: discountRecoveryContext?.countryCode,
    payoutMethod,
    currencyCode: discountRecoveryContext?.currencyCode,
    isOnlyCurrency: discountRecoveryContext?.isOnlyCurrency,
    sendAmount: discountRecoveryContext?.sendAmount,
    receiverAmount: discountRecoveryContext?.receiverAmount,
    feeAmount,
    cityId: discountRecoveryContext?.cityId,
    paymentMethod,
  });
};

export const getTransactionDiscountAutoApplyPolicy = ({
  isCurrentDiscountsLoaded,
  isAutoApplyBlocked,
  hasDefaultAutoApplyFailed,
  onApplyError,
}) => ({
  isReady: Boolean(isCurrentDiscountsLoaded),
  isBlocked: Boolean(isAutoApplyBlocked || hasDefaultAutoApplyFailed),
  onApplyError,
});

export const shouldShowTransactionDiscountEligibilityToast = ({
  hasAmount,
  isShowCalculatorForm,
  isCurrentDiscountsLoaded,
  isApplyingDiscount,
  isRevalidationPending = false,
  isDiscountLoadingHidden = true,
  discountDefault,
  failedDiscount,
  discountApplied,
  isSystemDiscountFailure = false,
}) => {
  if (!hasAmount) return false;
  if (!isShowCalculatorForm) return false;
  if (!isCurrentDiscountsLoaded) return false;
  if (isApplyingDiscount) return false;
  // A context-change revalidation is carried but not yet confirmed: do not flash
  // the premature APPLY_SUCCESS toast for the stale-but-retained discountApplied.
  if (isRevalidationPending) return false;
  if (!isDiscountLoadingHidden) return false;
  // A backend/system failure is shown by the discount failure section, not the
  // eligibility toast.
  if (isSystemDiscountFailure) return false;
  if (discountDefault && !failedDiscount && !discountApplied) return false;

  return true;
};

// A discount invalidated at Review & Pay must stay invalid for the whole
// transaction. Its code is tracked in the top-level
// `addTransactionInformation.discountHardInvalidCodes` (seeded empty at the
// new-transaction reset; kept off paymentMethod so it never leaks into a step's
// information/validation objects). The server active list does not
// know about it, so on every fresh fetch we re-stamp those codes with the same
// `applyErrorCode`/`inactive` marker the apply-failure path uses. That keeps them
// out of the available and auto-apply lists (deriveTransactionDiscountLists
// already drops applyErrorCode discounts) for the rest of the transaction.
const HARD_INVALID_ERROR_CODE = 'HARD_INVALID';

export const stampHardInvalidDiscounts = (discounts, hardInvalidCodes = []) => {
  const list = isArray(discounts) ? discounts : [];
  if (!isArray(hardInvalidCodes) || !hardInvalidCodes.length) return list;
  return list.map((discount) =>
    hardInvalidCodes.includes(discount?.code)
      ? {
          ...discount,
          applyErrorCode: discount?.applyErrorCode || HARD_INVALID_ERROR_CODE,
          inactive: true,
          hardInvalid: true,
        }
      : discount,
  );
};

export const fetchDiscountsAvailable = async ({
  token,
  countryCode,
  payoutMethod,
  currencyCode,
  isOnlyCurrency,
  sendAmount,
  receiverAmount,
  paymentMethod = '',
  cityId,
  discountFetchKey,
  discountDeriveKey,
  callback = FUNC_DEFAULT,
  // Optional Close handler for the system-error modal (used on Step 2 to reset
  // the step). Left undefined by non-Step-2 callers so their Close is unchanged.
  onSystemErrorClose,
  // Optional supersession guard. getDiscountsActive is not cancellable, so when
  // the fetch context (e.g. currency) changes faster than the request resolves,
  // several fetches race. A stale one resolving last would write its own
  // discountFetchKey and revert the store, hiding the discount section. The
  // recovery hook passes a checker that reports whether this fetch's key is no
  // longer the active one; if so we drop the result instead of committing it.
  // Defaults to never-stale so other callers are unchanged.
  isStale,
}) => {
  try {
    const { data } = await api.getDiscountsActive(token);

    // Superseded by a newer fetch (context changed while this request was in
    // flight): discard without dispatching or resolving loaded — the newer
    // fetch owns the store state and its own callback.
    if (isFunction(isStale) && isStale()) {
      return;
    }

    const hardInvalidCodes =
      stateStore?.addTransactionInformation?.discountHardInvalidCodes || [];
    const activeDiscounts = stampHardInvalidDiscounts(data, hardInvalidCodes);

    const feeAmount = getDiscountTxnFeeAmount();

    const { discountsAvailable, discountsInactive } =
      deriveTransactionDiscountLists({
        discounts: activeDiscounts,
        countryCode,
        payoutMethod,
        currencyCode,
        isOnlyCurrency,
        sendAmount,
        receiverAmount,
        feeAmount,
        cityId,
        paymentMethod,
      });

    dispatchStore({
      type: SET_ADD_TRANSACTION_INFORMATION,
      payload: {
        ...stateStore?.addTransactionInformation,
        // Cache the raw active list (amount-independent) so later amount edits
        // can re-filter it client-side without another server call. Hard-invalid
        // codes are already re-stamped on it so the marker survives this fetch.
        // These recovery keys/list live at the top level (siblings of
        // paymentMethod) so a step-navigation `paymentMethod: information`
        // dispatch cannot drop them.
        discountsActive: activeDiscounts,
        ...(discountFetchKey && { discountFetchKey }),
        ...(discountDeriveKey && { discountDeriveKey }),
        // Durable snapshot of the filtered lists for this derive context. The
        // volatile paymentMethod.discountsAvailable/Inactive are deleted by step
        // navigation (handleNextStep), so on a return-to-Step-2 recovery we
        // restore this snapshot verbatim rather than re-deriving. Re-deriving is
        // NOT faithful: filterDiscountsAvailable also depends on feeAmount, which
        // is not part of the derive key and legitimately changes once a payment
        // method sets the fee (e.g. a FLAT_FEE discount ineligible at fee 0 becomes
        // eligible at fee 8.1), so a re-derive would silently reclassify discounts.
        // A fresh fetch/re-derive supersedes any pending restore.
        discountFilteredSnapshot: {
          discountsAvailable,
          discountsInactive,
          discountDeriveKey,
        },
        discountRequiresRestore: false,
        paymentMethod: {
          ...stateStore?.addTransactionInformation?.paymentMethod,
          discountsAvailable,
          discountsInactive,
          discountApplied: null,
          discountDetails: null,
        },
      },
    });

    if (isFunction(callback)) {
      callback();
    }

    dispatchStore({ type: SET_SHOW_LOADING, payload: false });
  } catch (error) {
    checkMaintenance(error);
    // DEV-1320: surface the system-error modal for a network/transport failure
    // (isSystemNetworkError, httpErrors off) and for HTTP 5xx system failures.
    // A 400 is a client/validation response, not a system failure, so it is left
    // out (httpErrors stays off for it). 503 is owned by checkMaintenance and
    // resolves to null here anyway.
    const failureStatus = error?.response?.status;
    const isServerSystemFailure =
      typeof failureStatus === 'number' && failureStatus >= 500;
    checkSystemError(error, onSystemErrorClose, {
      httpErrors: isServerSystemFailure,
    });
    console.error(error?.message);

    dispatchStore({ type: SET_SHOW_LOADING, payload: false });

    if (isFunction(callback)) {
      callback();
    }
  }
};

export const useTransactionDiscountRecovery = ({
  countryConfig,
  currentCurrencySelected,
  currentReceiver,
  information,
  isAddAnotherReceivingCurrency,
  isDisabled,
  // When true, force one server refetch for the current context even if a valid
  // list is cached. General escape hatch for context changes the fetch key does
  // not capture; it is no longer wired to the return-to-Step-2 case, which the
  // fetch key now handles by including receiver identity. Defaults false.
  forceRefetch = false,
  // False until the host form has re-hydrated its local `information` from the
  // store on (re)mount. While false the recovery is skipped, so the transient
  // where `information` is still initialFormField (amounts 0) on a return to
  // Step 2 does not trigger a spurious client re-derive (which would clear the
  // applied discount and re-arm auto-apply — a visible "re-check" on Back).
  // Defaults true so callers that don't gate keep prior behaviour.
  isReady = true,
  paymentMethod,
  payoutMethod,
  setFetching = FUNC_DEFAULT,
  token,
}) => {
  // Reactive so the dedicated revalidation effect below re-runs when the
  // rate/amount finishes settling (the fire is gated on !isCalculatorRounding).
  const { isCalculatorRounding } = useCalculatorRoundingStore();
  const activeDiscountFetchKeyRef = useRef('');
  const activeDiscountDeriveKeyRef = useRef('');
  // DEV-1320: when a discount is already applied and the amount/currency
  // changes, we revalidate that specific applied discount instead of discarding
  // it and auto-applying the default. handleApplyDiscount/handleApplyDiscountError
  // are declared after this recovery effect, so hold them in refs the effect's
  // async fetch callback / synchronous re-derive branch can call without a
  // declaration-order (TDZ) or dependency-array problem. Assigned below.
  const handleApplyDiscountRef = useRef(null);
  const handleApplyDiscountErrorRef = useRef(null);
  // True while a context-change revalidation apply is in flight. A currency
  // change fires two recovery cycles (the fetch for the new currency, then a
  // re-derive when the fresh rate recomputes the receive amount); this flag lets
  // the second cycle know a revalidation already owns the applied discount so it
  // does not fall into the Case-2 default-apply path during the in-flight window.
  const isRevalidatingRef = useRef(false);
  // The discount being revalidated, preserved across the brief window where the
  // store's discountApplied has been nulled by the fetch but revalidation has not
  // yet resolved, so a later cycle can still target the right code. Cleared when
  // revalidation resolves (success or failure) or when a change leaves nothing
  // applied (Case 2).
  const revalidateTargetRef = useRef(null);
  // The derive key a context-change revalidation was last fired for, so the
  // settled-point trigger runs exactly once per context and never re-fires while
  // the same context is stable.
  const revalidatedDeriveKeyRef = useRef('');
  const [isDiscountsLoaded, setDiscountsLoaded] = useState(false);
  const [isAutoApplyBlocked, setAutoApplyBlocked] = useState(false);
  const [hasDefaultAutoApplyFailed, setDefaultAutoApplyFailed] =
    useState(false);
  const [failedDiscount, setFailedDiscount] = useState(null);
  const [failedDiscountErrorCode, setFailedDiscountErrorCode] = useState('');
  const [isApplyingDiscount, setApplyingDiscount] = useState(false);
  // Display-only: true while a context-change revalidation of an already-applied
  // discount is carried but not yet confirmed (Case 1). Deliberately distinct
  // from isApplyingDiscount (which also drives the DiscountLoading overlay,
  // drawer loading, and section reveal) so consumers can suppress the *premature*
  // applied badge / success toast during the window where discountApplied is
  // retained for the new context but revalidation has not yet settled — without
  // gating the loader/overlay/calculator. Cleared when revalidation resolves.
  const [isRevalidationPending, setRevalidationPending] = useState(false);
  const [isOpenFailedDiscountModal, setOpenFailedDiscountModal] =
    useState(false);
  // DEV-1320: true once the active-discount-list fetch has failed with a system
  // error (network/transport or HTTP 5xx) AND the user has closed the resulting
  // system-error modal. It drives FailureSection in the discount section and is
  // set from the fetch's onSystemErrorClose handler (not the catch), so the
  // section appears only after the modal is dismissed. Cleared on retry and on
  // any subsequent valid fetch/re-derive/stored-list path for a new context.
  const [hasActiveFetchFailed, setActiveFetchFailed] = useState(false);
  // Bumped by onRetryActiveListFetch to force the recovery effect to refetch the
  // active list even though the fetch context is unchanged (a retry keeps the
  // same key). The retry also clears the fetch-key refs so the fetch-action
  // decider sees a mismatch and re-fetches.
  const [activeFetchRetryNonce, setActiveFetchRetryNonce] = useState(0);

  const discountRecoveryContext = useMemo(
    () =>
      getTransactionDiscountRecoveryContext({
        countryCode: countryConfig?.countryCode,
        payoutMethod,
        currencyCode: currentCurrencySelected?.currencyCode,
        isOnlyCurrency: !isAddAnotherReceivingCurrency,
        sendAmount: information?.[ADD_TRANSACTION_YOU_SEND_NAME],
        sendAnotherAmount: information?.[ADD_TRANSACTION_YOU_SEND_ANOTHER_NAME],
        receiverAmount: information?.[ADD_TRANSACTION_RECEIVER_GET_NAME],
        cityId: currentReceiver?.address?.city?.id,
        receiverId: currentReceiver?.receiverId,
      }),
    [
      countryConfig?.countryCode,
      payoutMethod,
      currentCurrencySelected?.currencyCode,
      isAddAnotherReceivingCurrency,
      information,
      currentReceiver?.address?.city?.id,
      currentReceiver?.receiverId,
    ],
  );

  const isCurrentDiscountsLoaded = isTransactionDiscountRecoveryLoaded({
    discountRecoveryContext,
    isDiscountsLoaded,
    storedFetchKey: stateStore?.addTransactionInformation?.discountFetchKey,
  });

  useEffect(() => {
    if (!isReady) return;
    if (isDisabled) return;
    if (isEmpty(countryConfig)) return;
    if (!token) return;
    if (!discountRecoveryContext) return;

    const sendAmount = Number(discountRecoveryContext?.sendAmount || 0) || 0;
    const receiverAmount =
      Number(discountRecoveryContext?.receiverAmount || 0) || 0;
    if (!sendAmount || !receiverAmount) {
      // Empty/0 Send or Receive: nothing to discount. Remove any applied
      // discount, hide eligibility, and block auto-apply. Do NOT touch
      // activeDiscountFetchKeyRef — the fetch key excludes amounts, so writing it
      // here would make the next real fetch a no-op (activeKey === fetchKey) and
      // the list would never load. Only the derive ref is reset so re-entering an
      // amount re-derives from the still-cached discountsActive list (no server
      // call).
      activeDiscountDeriveKeyRef.current = '';

      const hasDiscountStateToClear =
        !isEmpty(paymentMethod?.discountApplied) ||
        !isEmpty(paymentMethod?.discountsAvailable) ||
        !isEmpty(paymentMethod?.discountsInactive) ||
        !isEmpty(paymentMethod?.discountDetails);
      if (hasDiscountStateToClear) {
        dispatchStore({
          type: SET_ADD_TRANSACTION_INFORMATION,
          payload: {
            ...stateStore?.addTransactionInformation,
            discountDeriveKey: '',
            paymentMethod: {
              ...stateStore?.addTransactionInformation?.paymentMethod,
              discountsAvailable: [],
              discountsInactive: [],
              discountApplied: null,
              discountDetails: null,
            },
          },
        });
      }

      setAutoApplyBlocked(true);
      setDefaultAutoApplyFailed(false);
      setFailedDiscount(null);
      setFailedDiscountErrorCode('');
      setOpenFailedDiscountModal(false);
      setActiveFetchFailed(false);
      setDiscountsLoaded(false);
      return;
    }

    // Discount-recovery keys/list live at the top level of
    // addTransactionInformation (siblings of paymentMethod). Read them from the
    // live store so a step-navigation dispatch that replaced paymentMethod does
    // not affect them.
    const storedInfo = stateStore?.addTransactionInformation;

    // Once any discount is hard-invalidated at Review & Pay this transaction,
    // default auto-apply stays off for the rest of the transaction (the user
    // picks manually) — the same policy withDiscountAutoApply enforces. Keep the
    // auto-apply block set on an amount re-derive/refetch instead of clearing it,
    // otherwise the parent's default-apply-pending signal (which still sees the
    // hard-invalidated default sitting in the inactive list) never resolves and
    // the discount section stays hidden behind its validating placeholder.
    const hasTxnHardInvalidation =
      (storedInfo?.discountHardInvalidCodes?.length || 0) > 0;

    // DEV-1320: determine the discount to revalidate for this context change.
    // Normally it is the discount applied *before* the change (the currency-select
    // handler does not touch discountApplied, and this effect runs before the
    // fetch/re-derive below nulls it). If a revalidation is already in flight from
    // an earlier cycle of the same change, the store's discountApplied may already
    // be null, so fall back to the in-flight target. When a target exists this is
    // Case 1: revalidate that exact code against the new amount/currency instead
    // of running the default auto-apply flow (Case 2). On success the same
    // discount is re-applied and the default stays blocked (a user-chosen discount
    // governs); on failure handleApplyDiscountError removes it, shows the
    // eligibility failure toast, moves it to the inactive list, and keeps the
    // default blocked so the section stays available for a manual pick.
    const appliedBeforeChange = paymentMethod?.discountApplied || null;
    // The discount to revalidate for this context change: the live applied one,
    // or a target carried from an earlier cycle of the same change. A currency
    // switch fires a fetch cycle and then a re-derive cycle (when the fresh rate
    // recomputes the receive amount), and the fetch has nulled discountApplied by
    // the time the re-derive runs, so the carry preserves the code across cycles.
    const revalidateTarget = !isEmpty(appliedBeforeChange)
      ? appliedBeforeChange
      : revalidateTargetRef.current;
    const hasAppliedBeforeChange = !isEmpty(revalidateTarget);

    const {
      discountFetchKey,
      discountDeriveKey,
      hasStoredDefaultApplyFailure,
      shouldFetch,
      shouldUseStoredDiscounts,
      shouldRederive,
    } = getTransactionDiscountRecoveryFetchAction({
      discountRecoveryContext,
      activeDiscountFetchKey: activeDiscountFetchKeyRef.current,
      activeDiscountDeriveKey: activeDiscountDeriveKeyRef.current,
      forceRefetch,
      paymentMethod,
      storedFetchKey: storedInfo?.discountFetchKey,
      storedDeriveKey: storedInfo?.discountDeriveKey,
      storedDiscountsActive: storedInfo?.discountsActive,
    });

    // DEV-1320: a Review & Pay (or other off-Step-2) hard-invalidation moved the
    // failed code to inactive inside the durable discountFilteredSnapshot, but the
    // volatile paymentMethod.discountsAvailable/Inactive were deleted by step
    // navigation (handleNextStep). Restore the snapshot verbatim here so the drawer
    // shows exactly the pre-submission classification with the failed code now
    // inactive. Do NOT re-derive: filterDiscountsAvailable also depends on feeAmount,
    // which is not part of the derive key and legitimately changes once a payment
    // method sets the fee, so a re-derive would silently reclassify unrelated
    // discounts (e.g. a FLAT_FEE offer ineligible at fee 0 becomes eligible at fee
    // 8.1). Only restore when the snapshot was built for the current context (derive
    // key matches); otherwise the amount genuinely changed, so fall through to the
    // fetch/re-derive paths, which rebuild the lists and clear this flag. Runs once.
    const filteredSnapshot = storedInfo?.discountFilteredSnapshot;
    if (
      storedInfo?.discountRequiresRestore &&
      !shouldFetch &&
      filteredSnapshot?.discountDeriveKey === discountDeriveKey &&
      Array.isArray(filteredSnapshot?.discountsInactive)
    ) {
      activeDiscountFetchKeyRef.current = discountFetchKey;
      activeDiscountDeriveKeyRef.current = discountDeriveKey;

      dispatchStore({
        type: SET_ADD_TRANSACTION_INFORMATION,
        payload: {
          ...stateStore?.addTransactionInformation,
          discountRequiresRestore: false,
          discountDeriveKey,
          paymentMethod: {
            ...stateStore?.addTransactionInformation?.paymentMethod,
            discountsAvailable: filteredSnapshot.discountsAvailable || [],
            discountsInactive: filteredSnapshot.discountsInactive || [],
          },
        },
      });

      // A hard-invalidation keeps default auto-apply blocked for the rest of the
      // transaction (the user picks manually), matching the shouldRederive branch.
      setAutoApplyBlocked(hasTxnHardInvalidation);
      setDefaultAutoApplyFailed(false);
      setActiveFetchFailed(false);
      setDiscountsLoaded(true);
      return;
    }

    if (!shouldFetch && !shouldUseStoredDiscounts && !shouldRederive) {
      // A net-zero amount edit (e.g. 50 -> 5 -> 50) clears isDiscountsLoaded via
      // handleAmountChangeStart on each keystroke but produces no derive-key
      // change, so no fetch/rederive/stored-swap runs to restore it. When the
      // stored filtered lists are still valid for the current amount (derive key
      // matches), re-assert loaded so the discount section (hidden during the
      // edit) reappears instead of staying hidden.
      if (storedInfo?.discountDeriveKey === discountDeriveKey) {
        setDiscountsLoaded(true);
      }
      // Revalidation of a carried applied discount is fired by the dedicated
      // effect below (keyed on the settled context + isCalculatorRounding), not
      // here — the fetch-commit / re-derive / rate-settle cycles of a currency
      // change do not reliably align in a single main-effect run.
      return;
    }

    if (shouldUseStoredDiscounts) {
      activeDiscountFetchKeyRef.current = discountFetchKey;
      activeDiscountDeriveKeyRef.current = discountDeriveKey;
      setDiscountsLoaded(true);
      // Keep the default blocked if a discount is applied / being revalidated for
      // this context, so this stored-list re-assert cannot re-open the default
      // auto-apply flow underneath an in-flight revalidation.
      setAutoApplyBlocked(
        hasStoredDefaultApplyFailure ||
          hasTxnHardInvalidation ||
          hasAppliedBeforeChange,
      );
      setDefaultAutoApplyFailed(hasStoredDefaultApplyFailure);
      // A valid cached list for this context clears any prior fetch failure.
      setActiveFetchFailed(false);
      return;
    }

    // Amounts changed but the server list for this context is already cached:
    // re-filter it locally (no server call). Amount changes re-attempt default
    // auto-apply per the PRD, so clear any prior default-apply failure block —
    // mirroring the reset the fresh-fetch path performs.
    if (shouldRederive) {
      activeDiscountFetchKeyRef.current = discountFetchKey;
      activeDiscountDeriveKeyRef.current = discountDeriveKey;
      // Case 1: carry the applied discount so the settled-point trigger below
      // revalidates it once. Case 2: nothing applied, clear any carry so the
      // default auto-apply flow runs as before. A new context supersedes any
      // prior in-flight revalidation, so clear the in-flight flag too (guards
      // against it sticking if a revalidation resolved via the system-error or
      // empty-data path, which calls neither success nor error handler).
      revalidateTargetRef.current = hasAppliedBeforeChange
        ? revalidateTarget
        : null;
      // Re-arm the once-per-context revalidation trigger for this new context.
      // revalidatedDeriveKeyRef latches the derive key it last fired for, but a
      // derive key recurs when the amount is toggled back and forth
      // (2000 -> 900 -> 2000 -> 900). Reaching this branch means the derive key
      // just changed (a genuine new context), so clearing the latch lets the
      // trigger fire again even when the value matches an earlier visit;
      // otherwise the second revalidation is suppressed and a stale applied
      // discount survives the amount change. isRevalidatingRef and the
      // revalidateTargetRef emptiness guard still prevent a duplicate fire
      // within this episode.
      revalidatedDeriveKeyRef.current = '';
      // Display-only: suppress the premature applied badge / success toast until
      // this carried revalidation resolves.
      setRevalidationPending(hasAppliedBeforeChange);
      isRevalidatingRef.current = false;

      const feeAmount = getDiscountTxnFeeAmount();
      const { discountsAvailable, discountsInactive } =
        refilterStoredTransactionDiscounts({
          discountRecoveryContext,
          rawDiscounts: storedInfo?.discountsActive,
          payoutMethod,
          feeAmount,
        });

      dispatchStore({
        type: SET_ADD_TRANSACTION_INFORMATION,
        payload: {
          ...stateStore?.addTransactionInformation,
          discountDeriveKey,
          // Refresh the durable snapshot for the new context and supersede any
          // pending restore: the amount genuinely changed, so this freshly
          // re-filtered split is the new source of truth for a later recovery.
          discountFilteredSnapshot: {
            discountsAvailable,
            discountsInactive,
            discountDeriveKey,
          },
          discountRequiresRestore: false,
          paymentMethod: {
            ...stateStore?.addTransactionInformation?.paymentMethod,
            discountsAvailable,
            discountsInactive,
            // Case 1 (a discount is applied): keep it in place while we
            // revalidate it against the new amount below; the DiscountLoading
            // overlay covers the brief window and revalidation overwrites it on
            // success or clears it on failure. Case 2 (nothing applied): clear
            // the applied/failed state so default auto-apply re-attempts for the
            // new amount, matching the reset the old amount-keyed fetch did.
            discountApplied: hasAppliedBeforeChange ? revalidateTarget : null,
            discountDetails: hasAppliedBeforeChange
              ? stateStore?.addTransactionInformation?.paymentMethod
                  ?.discountDetails
              : null,
          },
        },
      });

      // Case 1: block the default auto-apply so it cannot fire while we
      // revalidate the currently applied discount for the new amount.
      setAutoApplyBlocked(hasTxnHardInvalidation || hasAppliedBeforeChange);
      setDefaultAutoApplyFailed(false);
      setFailedDiscount(null);
      setFailedDiscountErrorCode('');
      setOpenFailedDiscountModal(false);
      // A successful local re-filter for this context clears any prior fetch
      // failure (the cached list is valid; no server call was needed).
      setActiveFetchFailed(false);
      setDiscountsLoaded(true);
      // Revalidation itself fires once from the settled point above, not here,
      // to avoid a duplicate request across a currency change's two cycles.
      return;
    }

    activeDiscountFetchKeyRef.current = discountFetchKey;
    activeDiscountDeriveKeyRef.current = discountDeriveKey;
    // Case 1: a discount is applied for the previous context. Carry it for
    // revalidation and block the default auto-apply up front so it cannot fire in
    // the window where the fresh fetch has nulled discountApplied but revalidation
    // has not yet run. Case 2: nothing applied, clear any carry and let the normal
    // default auto-apply flow run once the list loads.
    revalidateTargetRef.current = hasAppliedBeforeChange ? revalidateTarget : null;
    // Re-arm the once-per-context revalidation trigger for this new context (same
    // reason as the shouldRederive branch above): the latched derive key recurs
    // when the amount toggles back and forth, and reaching this fresh-fetch branch
    // means a genuine new context, so the trigger must be allowed to fire again.
    revalidatedDeriveKeyRef.current = '';
    // Display-only: suppress the premature applied badge / success toast until
    // this carried revalidation resolves.
    setRevalidationPending(hasAppliedBeforeChange);
    // A new context supersedes any prior in-flight revalidation; clear the flag so
    // it cannot stick (the system-error / empty-data paths call neither handler).
    isRevalidatingRef.current = false;
    setAutoApplyBlocked(hasTxnHardInvalidation || hasAppliedBeforeChange);
    setDefaultAutoApplyFailed(false);
    setFailedDiscount(null);
    setFailedDiscountErrorCode('');
    setOpenFailedDiscountModal(false);
    // Clear any prior fetch failure before a fresh attempt; it is re-set only if
    // this attempt fails and the user closes the system-error modal.
    setActiveFetchFailed(false);
    setDiscountsLoaded(false);
    setFetching(true);

    fetchDiscountsAvailable({
      token,
      ...discountRecoveryContext,
      payoutMethod,
      discountFetchKey,
      discountDeriveKey,
      callback: () => {
        setDiscountsLoaded(true);
        setFetching(false);
        // Revalidation (Case 1) is fired by the dedicated effect below once the
        // context settles, not here — a currency change fires this fetch and then
        // a re-derive when the new rate updates the receive amount, and the
        // settle can land in either cycle depending on network timing.
      },
      // DEV-1320: on an active-list fetch failure, closing the system-error
      // modal surfaces FailureSection (with a retry) in the discount section
      // instead of resetting Step 2 — the send/receive amounts are preserved.
      // This differs from the quote/calculator path, whose close still resets.
      onSystemErrorClose: () => setActiveFetchFailed(true),
      // Drop this fetch's result if a newer context (e.g. a subsequent currency
      // switch) has since fired its own fetch — activeDiscountFetchKeyRef is
      // advanced synchronously before each fetch, so a mismatch means this one
      // was superseded and must not overwrite the current context's discounts.
      isStale: () => activeDiscountFetchKeyRef.current !== discountFetchKey,
    });
  }, [
    isReady,
    forceRefetch,
    isDisabled,
    countryConfig,
    token,
    discountRecoveryContext,
    paymentMethod,
    payoutMethod,
    setFetching,
    // Retry re-runs this effect; the refs are cleared in the handler so the
    // fetch-action decider sees a key mismatch and refetches the same context.
    activeFetchRetryNonce,
  ]);

  // Fire the revalidation of a carried applied discount (Case 1) exactly once per
  // settled context. checkDiscountAppliedValid is uncancellable, so it must run a
  // single time, at the settled amount. On success the same discount is re-applied
  // and the default stays blocked (the user's discount governs); on failure
  // handleApplyDiscountError removes it, shows the eligibility toast, moves it to
  // the inactive list, and keeps the default blocked so the section stays
  // available for a manual pick.
  const fireRevalidateAppliedDiscount = useCallback(() => {
    const item = revalidateTargetRef.current;
    if (isEmpty(item)) return;
    isRevalidatingRef.current = true;
    handleApplyDiscountRef.current?.({
      token,
      item,
      // Auto/context-driven revalidation, not a manual apply: uses the local
      // DiscountLoading overlay (isApplyingDiscount), not the global screen.
      onApplyError: (errorCode, failedItem) => {
        isRevalidatingRef.current = false;
        revalidateTargetRef.current = null;
        setRevalidationPending(false);
        handleApplyDiscountErrorRef.current?.(errorCode, failedItem || item);
      },
      onApplySuccess: () => {
        isRevalidatingRef.current = false;
        revalidateTargetRef.current = null;
        setRevalidationPending(false);
      },
    });
  }, [token]);

  // Dedicated revalidation trigger. The main effect above only carries the applied
  // discount (revalidateTargetRef) and blocks the default; the actual apply fires
  // here, once the context has genuinely settled. Keying on isCalculatorRounding
  // and the derive context (via isCurrentDiscountsLoaded / discountRecoveryContext)
  // makes this re-run across the fetch-commit / re-derive / rate-settle cycles of
  // a currency change, so the fire lands exactly once at the settled amount and is
  // never missed if the settle and the rate-settle happen in different cycles.
  useEffect(() => {
    if (isDisabled) return;
    if (isCalculatorRounding) return;
    if (isRevalidatingRef.current) return;
    if (isEmpty(revalidateTargetRef.current)) return;
    const currentDeriveKey = discountRecoveryContext?.discountDeriveKey;
    if (!currentDeriveKey) return;
    // The derived list must match the current context (not mid fetch/re-derive).
    if (
      stateStore?.addTransactionInformation?.discountDeriveKey !==
      currentDeriveKey
    )
      return;
    if (!isDiscountsLoaded) return;
    if (revalidatedDeriveKeyRef.current === currentDeriveKey) return;
    // Guard against the stale settle of a currency change. On a currency switch
    // the store's rate updates to the new currency BEFORE the receive amount is
    // recomputed, so there is a transient settle where the derive key already has
    // the new currency but still the previous currency's receive amount — and
    // isCalculatorRounding can flicker false in that window. Revalidating there
    // would validate against the wrong amount. The receive amount is settled only
    // when it is consistent with the current rate (receive ~= send * rate); until
    // then, skip. Only checked for single-currency (where the relationship holds)
    // and when a numeric rate/amount is available; otherwise rely on the guards
    // above.
    const rmt = stateStore?.addTransactionInformation?.remittance;
    const rateValue = Number(rmt?.currentRateStore?.rateValue || 0);
    const sendAmt = Number(discountRecoveryContext?.sendAmount || 0);
    const recvAmt = Number(discountRecoveryContext?.receiverAmount || 0);
    const amountSettledForRate =
      !discountRecoveryContext?.isOnlyCurrency ||
      rateValue <= 0 ||
      recvAmt <= 0 ||
      Math.abs(recvAmt - sendAmt * rateValue) <= recvAmt * 0.1;
    if (!amountSettledForRate) return;
    revalidatedDeriveKeyRef.current = currentDeriveKey;
    fireRevalidateAppliedDiscount();
  }, [
    isDisabled,
    isCalculatorRounding,
    isDiscountsLoaded,
    discountRecoveryContext,
    // paymentMethod changes on every fetch-commit / re-derive dispatch, so this
    // effect re-evaluates the stored-derive-key match at those transitions too
    // (not only on rate-settle or context change).
    paymentMethod,
    fireRevalidateAppliedDiscount,
  ]);

  const handleApplyDiscountError = useCallback((errorCode, item) => {
    // The failed item may be a minimal applied-discount response: a context-change
    // revalidation carries the prior discountApplied (only code/discountAmount)
    // into this path. Resolve it to the canonical active-list entry by code so
    // failedDiscount — which feeds the failure-details modal (DiscountModal) and,
    // via markDiscountApplyFailed, the inactive DiscountTicket — has the full
    // display shape (descriptions, discountMethod, status, dates). An applied-shape
    // object has no `status`, so those surfaces render a broken ticket. Fall back
    // to the passed item when the code is not in the cached list. (markDiscount-
    // ApplyFailed does its own lookup too; resolving here keeps failedDiscount and
    // the inactive entry consistent.)
    const activeList =
      stateStore?.addTransactionInformation?.discountsActive || [];
    const canonicalItem =
      (item?.code && activeList.find((discount) => discount?.code === item.code)) ||
      item ||
      null;

    setFailedDiscount(canonicalItem);
    setFailedDiscountErrorCode(errorCode || '');
    setAutoApplyBlocked(true);
    setDefaultAutoApplyFailed(
      (hasFailed) => hasFailed || Boolean(canonicalItem?.isDefault),
    );

    if (canonicalItem) {
      markDiscountApplyFailed({ item: canonicalItem, errorCode });
    }
  }, []);

  // Keep the ref the recovery effect's revalidation error path uses in sync.
  handleApplyDiscountErrorRef.current = handleApplyDiscountError;

  const handleApplyDiscount = useCallback(
    ({
      onApplyError,
      onApplySuccess,
      isManual = false,
      onSystemError,
      ...payload
    }) => {
      handleApplyDiscountBeforePaymentMethod({
        ...payload,
        isManual,
        onSystemError,
        // Manual applies drive the global LoadingScreen (via isGlobalLoading
        // Disabled: false below) instead of the local DiscountLoading overlay,
        // so skip the local isApplyingDiscount flag for them. Auto/default
        // applies keep the local overlay.
        onApplyStart: () => {
          if (!isManual) setApplyingDiscount(true);
        },
        onApplyEnd: () => {
          if (!isManual) setApplyingDiscount(false);
        },
        onApplyError: (errorCode, item) => {
          if (onApplyError) {
            onApplyError(errorCode, item || payload.item);
            return;
          }

          handleApplyDiscountError(errorCode, item || payload.item);
        },
        onApplySuccess: () => {
          setFailedDiscount(null);
          setFailedDiscountErrorCode('');
          onApplySuccess?.();
        },
      });
    },
    [handleApplyDiscountError],
  );

  // Keep the ref the recovery effect uses for context-change revalidation in
  // sync with the latest handleApplyDiscount. Declared before the effect (TDZ),
  // assigned here after handleApplyDiscount exists.
  handleApplyDiscountRef.current = handleApplyDiscount;

  const handleRetryFailedDiscount = useCallback(() => {
    if (!failedDiscount) return;

    handleApplyDiscount({
      token,
      item: failedDiscount,
      onApplyError: handleApplyDiscountError,
    });
  }, [failedDiscount, handleApplyDiscount, handleApplyDiscountError, token]);

  const canViewFailedDiscountConditions = isFailedDiscountConditionsViewable({
    failedDiscount,
    errorCode: failedDiscountErrorCode,
  });

  const handleOpenFailedDiscountConditions = useCallback(() => {
    if (!canViewFailedDiscountConditions) return;
    setOpenFailedDiscountModal(true);
  }, [canViewFailedDiscountConditions]);

  const handleCloseFailedDiscountConditions = useCallback(() => {
    setOpenFailedDiscountModal(false);
  }, []);

  const handleAmountChangeStart = () => {
    setDiscountsLoaded(false);
    setAutoApplyBlocked(true);
  };

  // DEV-1320: FailureSection's retry — refetch the active discount list for the
  // current context. Clear the failure flag and the fetch-key refs (so the
  // fetch-action decider sees a key mismatch for the unchanged context and
  // re-fetches), then bump the nonce to re-run the recovery effect.
  const onRetryActiveListFetch = useCallback(() => {
    activeDiscountFetchKeyRef.current = '';
    activeDiscountDeriveKeyRef.current = '';
    setActiveFetchFailed(false);
    setActiveFetchRetryNonce((nonce) => nonce + 1);
  }, []);

  const autoApplyPolicy = useMemo(
    () =>
      getTransactionDiscountAutoApplyPolicy({
        isCurrentDiscountsLoaded,
        isAutoApplyBlocked,
        hasDefaultAutoApplyFailed,
        onApplyError: handleApplyDiscountError,
      }),
    [
      isCurrentDiscountsLoaded,
      isAutoApplyBlocked,
      hasDefaultAutoApplyFailed,
      handleApplyDiscountError,
    ],
  );

  return {
    autoApplyPolicy,
    canViewFailedDiscountConditions,
    failedDiscount,
    failedDiscountErrorCode,
    handleApplyDiscount,
    handleApplyDiscountError,
    handleAmountChangeStart,
    handleCloseFailedDiscountConditions,
    handleOpenFailedDiscountConditions,
    handleRetryFailedDiscount,
    hasActiveFetchFailed,
    onRetryActiveListFetch,
    isApplyingDiscount,
    isRevalidationPending,
    isCurrentDiscountsLoaded,
    isOpenFailedDiscountModal,
  };
};

export const fetchDiscountsActive = async ({
  token,
  countryCode,
  payoutMethod,
  currencyCode,
  isOnlyCurrency,
  sendAmount,
  receiverAmount,
  paymentMethod = '',
}) => {
  try {
    const { data } = await api.getDiscountsActive(token);

    const feeAmount = getDiscountTxnFeeAmount();

    const { discountsAvailable, discountsInactive } =
      deriveTransactionDiscountLists({
        discounts: data,
        countryCode,
        payoutMethod,
        currencyCode,
        isOnlyCurrency,
        sendAmount,
        receiverAmount,
        feeAmount,
        paymentMethod,
      });

    if (data && !arrayUtils.isArrayEmpty(data)) {
      dispatchStore({
        type: SET_ADD_TRANSACTION_INFORMATION,
        payload: {
          ...stateStore.addTransactionInformation,
          paymentMethod: {
            ...stateStore.addTransactionInformation.paymentMethod,
            discountsAvailable,
            discountsInactive,
            discountApplied: null,
          },
        },
      });
    }

    dispatchStore({ type: SET_SHOW_LOADING, payload: false });
  } catch (error) {
    checkMaintenance(error);
    console.error(error?.message);
    dispatchStore({ type: SET_SHOW_LOADING, payload: false });
  }
};

export const fetchDiscountsActiveWithoutPaymentMethod = async ({
  token,
  countryCode,
  payoutMethod,
  currencyCode,
  isOnlyCurrency,
  sendAmount,
  receiverAmount,
}) => {
  fetchDiscountsActive({
    token,
    countryCode,
    payoutMethod,
    currencyCode,
    isOnlyCurrency,
    sendAmount,
    receiverAmount,
    paymentMethod: '',
  });
};

export const checkDiscountAppliedValid = async (
  token,
  {
    code,
    countryCode,
    currencies,
    deliveryMethod,
    paymentMethod,
    receivingAmount,
    txnAmount,
    txnFee,
    cityId,
    isGlobalLoadingDisabled = false,
  },
  onApplyError,
  onApplySuccess = () => {},
  // Optional handler for a transport/system failure (offline, dropped
  // connection). When the error is a system error the caller (e.g. the drawer)
  // uses this to close itself; the system-error modal is surfaced here.
  onSystemError,
) => {
  if (!isGlobalLoadingDisabled) {
    dispatchStore({ type: SET_SHOW_LOADING, payload: true });
  }
  try {
    const { data } = await api.applyDiscount({
      token,
      code,
      countryCode,
      currencies,
      deliveryMethod,
      paymentMethod,
      receivingAmount,
      txnAmount,
      txnFee,
      cityId,
    });

    if (data) {
      const statePayload = {
        ...stateStore.addTransactionInformation,
        paymentMethod: {
          ...stateStore.addTransactionInformation.paymentMethod,
          discountApplied: data,
        },
      };

      dispatchStore({
        type: SET_ADD_TRANSACTION_INFORMATION,
        payload: statePayload,
      });

      if (!isGlobalLoadingDisabled) {
        dispatchStore({ type: SET_SHOW_LOADING, payload: false });
      }

      onApplySuccess();
    }

    if (!isGlobalLoadingDisabled) {
      dispatchStore({ type: SET_SHOW_LOADING, payload: false });
    }
  } catch (error) {
    checkMaintenance(error);

    // A transport/system failure (offline, dropped connection) is not a discount
    // validation error: surface the system-error modal and let the caller close
    // the drawer, instead of routing through the discount-failure path (which
    // would wrongly mark the discount invalid on a transient network blip).
    if (checkSystemError(error)) {
      if (!isGlobalLoadingDisabled) {
        dispatchStore({ type: SET_SHOW_LOADING, payload: false });
      }
      onSystemError?.();
      return;
    }

    const { message, response } = error || {};
    const { data } = response || {};
    const { errorCode } = data || {};

    console.error(message);

    if (!isGlobalLoadingDisabled) {
      dispatchStore({ type: SET_SHOW_LOADING, payload: false });
    }

    onApplyError(errorCode || '');
  }
};

export const getDiscountsTypePromotion = (discounts) =>
  discounts.filter((d) => d.type === PROMOTION);
export const getDiscountsTypeVoucher = (discounts) =>
  discounts.filter((d) => d.type === VOUCHER);
export const getDiscountsTypeOther = (discounts) =>
  discounts.filter((d) => d.type !== PROMOTION && d.type === VOUCHER);

export const handleApplyDiscount = ({
  token,
  item,
  onApplyError,
  onApplySuccess = () => {},
}) => {
  let { code, country, currencyCode, deliveryMethod, paymentMethod } =
    item || {};

  const { addTransactionInformation, currentReceiver } = stateStore || {};
  const {
    receiverPayoutMethod,
    remittance,
    paymentMethod: paymentMethodStore,
  } = addTransactionInformation || {};

  country = currentReceiver?.country?.code;
  const cityId = currentReceiver?.address?.city?.id;
  currencyCode = remittance?.currentCurrencySelected?.currencyCode;
  deliveryMethod = receiverPayoutMethod?.[ADD_TRANSACTION_PAYOUT_METHOD_NAME];
  paymentMethod = paymentMethodStore?.[ADD_TRANSACTION_PAYMENT_METHOD_NAME];

  const sendAmount = numberUtils.mathRound(
    remittance[ADD_TRANSACTION_YOU_SEND_NAME] || 0,
  );
  const sendAnotherAmount = numberUtils.mathRound(
    remittance[ADD_TRANSACTION_YOU_SEND_ANOTHER_NAME] || 0,
  );
  const sendTotalAmount = numberUtils.mathRound(sendAmount + sendAnotherAmount);

  const receiverAmount = Number(
    remittance[ADD_TRANSACTION_RECEIVER_GET_NAME] || 0,
  );
  // DEV-1320: txnFee for discount validation is the fee.feeAmount from the /quote
  // response, sourced fresh from calculatorRoundingResponse rather than
  // remittance.feeAmount (which can be stale/0 at apply time).
  const txnFee = getDiscountTxnFeeAmount();

  const checkPayload = {
    code,
    countryCode: country,
    currencies: [currencyCode],
    deliveryMethod,
    paymentMethod: [paymentMethod],
    txnAmount: sendTotalAmount,
    receivingAmount: receiverAmount,
    txnFee,
    cityId,
  };
  checkDiscountAppliedValid(token, checkPayload, onApplyError, onApplySuccess);
};

export const handleApplyDiscountBeforePaymentMethod = ({
  token,
  item,
  isManual = false,
  onApplyError = () => {},
  onApplySuccess = () => {},
  onApplyStart = () => {},
  onApplyEnd = () => {},
  onSystemError,
}) => {
  const { code } = item || {};

  const { addTransactionInformation, currentReceiver } = stateStore || {};
  const { receiverPayoutMethod, remittance } = addTransactionInformation || {};

  const country = currentReceiver?.country?.code;
  const cityId = currentReceiver?.address?.city?.id;
  const currencyCode = remittance?.currentCurrencySelected?.currencyCode;
  const deliveryMethod =
    receiverPayoutMethod?.[ADD_TRANSACTION_PAYOUT_METHOD_NAME];

  const sendAmount = numberUtils.mathRound(
    remittance[ADD_TRANSACTION_YOU_SEND_NAME] || 0,
  );
  const sendAnotherAmount = numberUtils.mathRound(
    remittance[ADD_TRANSACTION_YOU_SEND_ANOTHER_NAME] || 0,
  );
  const sendTotalAmount = numberUtils.mathRound(sendAmount + sendAnotherAmount);

  const receiverAmount = Number(
    remittance[ADD_TRANSACTION_RECEIVER_GET_NAME] || 0,
  );
  // DEV-1320: txnFee for discount validation is the fee.feeAmount from the /quote
  // response, sourced fresh from calculatorRoundingResponse rather than
  // remittance.feeAmount (which can be stale/0 at apply time).
  const txnFee = getDiscountTxnFeeAmount();

  const checkPayload = {
    code,
    countryCode: country,
    currencies: [currencyCode],
    deliveryMethod,
    paymentMethod: [null],
    txnAmount: sendTotalAmount,
    receivingAmount: receiverAmount,
    txnFee,
    cityId,
    // Manual apply uses the global LoadingScreen; auto/default apply keeps it
    // disabled and shows the local DiscountLoading overlay instead.
    isGlobalLoadingDisabled: !isManual,
  };

  onApplyStart();
  return checkDiscountAppliedValid(
    token,
    checkPayload,
    (errorCode) => {
      onApplyEnd();
      onApplyError(errorCode || '', item);
    },
    () => {
      onApplyEnd();
      onApplySuccess();
    },
    () => {
      onApplyEnd();
      onSystemError?.();
    },
  );
};

export const markDiscountApplyFailed = ({
  item,
  errorCode,
  hardInvalid = false,
}) => {
  if (!item?.code) return;

  const { addTransactionInformation } = stateStore || {};
  const {
    paymentMethod,
    discountHardInvalidCodes = [],
    discountsActive = [],
  } = addTransactionInformation || {};
  const { discountsAvailable = [], discountsInactive = [] } =
    paymentMethod || {};

  // A Review & Pay submit failure hard-invalidates the discount for the rest of
  // the transaction: record its code so re-derivation, a fresh server fetch, and
  // auto-apply all keep treating it as invalid until the next transaction reset.
  // Ordinary auto-apply eligibility failures (hardInvalid = false) stay soft, so
  // an amount change can still re-attempt them.
  const nextHardInvalidCodes =
    hardInvalid && !discountHardInvalidCodes.includes(item.code)
      ? [...discountHardInvalidCodes, item.code]
      : discountHardInvalidCodes;

  // The failed item can arrive as a minimal applied-discount response rather
  // than a full active-list entry: a context-change revalidation carries the
  // prior discountApplied (only code/discountAmount) into this path. The inactive
  // DiscountTicket renders from the list shape (descriptions, discountMethod,
  // status, dates); an applied-shape item has no `status`, so it paints a broken
  // ticket (undefined background image, empty description). Prefer the canonical
  // raw-list entry by code (discountsActive is amount-independent and holds the
  // display shape) so the inactive row always renders correctly; fall back to the
  // passed item when the code is not in the cached list.
  const canonicalItem =
    discountsActive.find((discount) => discount?.code === item.code) || item;
  const failedDiscount = {
    ...canonicalItem,
    applyErrorCode: errorCode,
    inactive: true,
    ...(hardInvalid && { hardInvalid: true }),
  };
  // A hard-invalidation (Review & Pay failure) can run off Step 2, where step
  // navigation has already deleted the volatile paymentMethod filtered lists, so
  // building from them would drop every previously-inactive discount except this
  // failed one. The durable discountFilteredSnapshot holds the pre-submission
  // classification (it is a top-level sibling of discountsActive, untouched by
  // step navigation), so use it as the base on the hard path and move the failed
  // code into inactive. Soft auto-apply failures run on Step 2 with the volatile
  // lists intact, so they keep using them (unchanged behavior).
  const filteredSnapshot = addTransactionInformation?.discountFilteredSnapshot;
  const baseAvailable =
    hardInvalid && Array.isArray(filteredSnapshot?.discountsAvailable)
      ? filteredSnapshot.discountsAvailable
      : discountsAvailable;
  const baseInactive =
    hardInvalid && Array.isArray(filteredSnapshot?.discountsInactive)
      ? filteredSnapshot.discountsInactive
      : discountsInactive;

  const nextDiscountsAvailable = baseAvailable.filter(
    (discount) => discount?.code !== item.code,
  );
  const nextDiscountsInactive = [
    failedDiscount,
    ...baseInactive.filter((discount) => discount?.code !== item.code),
  ];
  // Stamp the failure onto the cached raw active list too. That list is the
  // amount-independent source re-derived on every amount edit, so the marker
  // (and therefore the discount's place in the inactive section) survives those
  // rebuilds until a fresh server fetch replaces the list.
  const nextDiscountsActive = discountsActive.map((discount) =>
    discount?.code === item.code
      ? {
          ...discount,
          applyErrorCode: errorCode,
          inactive: true,
          ...(hardInvalid && { hardInvalid: true }),
        }
      : discount,
  );

  dispatchStore({
    type: SET_ADD_TRANSACTION_INFORMATION,
    payload: {
      ...addTransactionInformation,
      discountHardInvalidCodes: nextHardInvalidCodes,
      discountsActive: nextDiscountsActive,
      // On the hard path, update the durable snapshot to the same failed-code-moved
      // classification and flag the recovery effect to restore it on return to
      // Step 2 (see the discountRequiresRestore branch). Returning trusts / restores
      // this snapshot verbatim rather than re-deriving, so the drawer shows exactly
      // the pre-submission classification with the failed code now inactive. The
      // snapshot keeps its original discountDeriveKey so the restore only fires when
      // the return context still matches. Soft failures (hardInvalid = false) run on
      // Step 2 with the volatile lists intact and do not touch the snapshot or flag.
      ...(hardInvalid && {
        discountFilteredSnapshot: {
          discountsAvailable: nextDiscountsAvailable,
          discountsInactive: nextDiscountsInactive,
          discountDeriveKey: filteredSnapshot?.discountDeriveKey,
        },
        discountRequiresRestore: true,
      }),
      paymentMethod: {
        ...paymentMethod,
        discountsAvailable: nextDiscountsAvailable,
        discountsInactive: nextDiscountsInactive,
        discountDetails: failedDiscount,
        discountApplied: null,
      },
    },
  });
};

// "Other Offer" availability. A discount counts as another usable offer as long
// as it is still in the fetched list and has neither expired nor already failed
// at transaction submission during this flow. It does NOT have to satisfy the
// current Step 2 eligibility rules: an offer that is merely inactive for the
// present amount/context is still selectable, so the user keeps the "Other
// Offer" path until every remaining discount is expired or submit-failed.
// Exclusions:
//   - the discount that just failed (`failedDiscount`),
//   - any code hard-invalidated by an earlier submit failure (`hardInvalidCodes`),
//   - expired discounts.
export const hasEligibleAlternativeDiscount = ({
  discountsAvailable,
  discountsInactive,
  failedDiscount,
  hardInvalidCodes = [],
}) => {
  const excludedCodes = new Set(
    [failedDiscount?.code, ...hardInvalidCodes].filter(Boolean),
  );

  const alternatives = getDiscountsActiveList({
    discountsAvailable,
    discountsInactive,
  }).filter(
    (discount) =>
      discount?.code &&
      !excludedCodes.has(discount.code) &&
      discount.status !== DISCOUNT_EXPIRED,
  );

  return !arrayUtils.isArrayEmpty(alternatives);
};

export const getReviewPayDiscountRecoveryAction = ({
  errorCode,
  paymentMethod: paymentMethodState,
}) => {
  const {
    discountsAvailable,
    discountsInactive,
    discountApplied,
    discountDetails,
  } = paymentMethodState || {};
  // Resolve the discount that actually failed at Review & Pay from the applied
  // discount first (its details from the active list, else the applied object).
  // discountDetails is only a last resort: it can hold a stale earlier failure
  // (e.g. a default auto-apply that failed before the user manually applied a
  // different discount), which would otherwise surface the wrong failed discount.
  const failedDiscount =
    findDiscountDetails(discountsAvailable, discountApplied?.code) ||
    discountApplied ||
    discountDetails;

  // Codes hard-invalidated earlier in this transaction (a prior Review & Pay
  // submit failure) live at the top level of addTransactionInformation. The
  // current failure is not recorded there until the user acts on this modal, so
  // it is excluded separately via failedDiscount.
  const hardInvalidCodes =
    stateStore?.addTransactionInformation?.discountHardInvalidCodes || [];

  const hasOtherEligibleDiscounts = hasEligibleAlternativeDiscount({
    discountsAvailable,
    discountsInactive,
    failedDiscount,
    hardInvalidCodes,
  });

  return {
    errorCode: errorCode || '',
    failedDiscount,
    hasOtherEligibleDiscounts,
    isCloseOnly: !hasOtherEligibleDiscounts,
    shouldViewOtherOffers: hasOtherEligibleDiscounts,
  };
};

export const handleReviewPayDiscountRecoveryViewOtherOffers = ({
  errorCode,
  failedDiscount,
  callback = FUNC_DEFAULT,
}) => {
  // The eligible alternatives already live in the store's discountsAvailable
  // (that is how hasOtherEligibleDiscounts was determined), so mark the failed
  // discount inactive client-side and navigate without refetching the active
  // list from the server.
  markDiscountApplyFailed({
    item: failedDiscount,
    errorCode,
    hardInvalid: true,
  });
  callback?.();
};

export const handleReviewPayDiscountRecoveryClose = ({
  errorCode,
  failedDiscount,
}) => {
  // Closing the Review & Pay failure modal still means the backend rejected this
  // discount for the current transaction. Keep that code inactive just like the
  // "Other offers" path so returning to Step 2 cannot auto-apply it again.
  markDiscountApplyFailed({
    item: failedDiscount,
    errorCode,
    hardInvalid: true,
  });
};

export const handleRemoveDiscountApplied = (payload) => {
  const { payoutMethod, paymentMethod, callback } = payload || {};

  const statePayload = {
    ...stateStore.addTransactionInformation,
    receiverPayoutMethod: {
      ...stateStore.addTransactionInformation.receiverPayoutMethod,
      ...(payoutMethod && {
        [ADD_TRANSACTION_PAYOUT_METHOD_NAME]: payoutMethod,
        currentPayoutItemSelectedIndex: -1,
      }),
    },
    paymentMethod: {
      ...stateStore.addTransactionInformation.paymentMethod,
      ...(paymentMethod && {
        [ADD_TRANSACTION_PAYMENT_METHOD_NAME]: paymentMethod,
      }),
      ...(Boolean(callback) && {
        discountsAvailable: [],
        discountsInactive: [],
      }),
      discountApplied: null,
    },
  };
  dispatchStore({
    type: SET_ADD_TRANSACTION_INFORMATION,
    payload: statePayload,
  });

  callback && callback();
};

export const handleOnCloseDiscountImportantPopup = ({ callback }) => {
  callback();
};

export const renderDiscountImportantPopup = ({
  t,
  isOpen,
  setOpen,
  payoutMethod,
  onCloseExt,
  onClickExt,
  contentReplace,
}) => (
  <InformPopup
    isOpen={isOpen}
    imageSrc={NoticeIcon}
    imagesStyles={{
      width: '75px',
      height: '60px',
      marginBottom: '8px',
    }}
    text={t('label_important')}
    content={contentReplace || t('discount_important_desc')}
    buttonLabel1={t('button_no')}
    onClose={() =>
      handleOnCloseDiscountImportantPopup({
        callback: () => {
          setOpen(false);
          onCloseExt && onCloseExt();
        },
      })
    }
    buttonLabel2={t('button_yes')}
    onClick={() =>
      handleRemoveDiscountApplied({
        payoutMethod,
        callback: () => {
          setOpen(false);
          onClickExt && onClickExt();
        },
      })
    }
    isBackdropOverlap={true}
    zIndexBackdropOverlap={1055}
    isClickBackdropToClose={false}
  />
);

export const findDiscountDetails = (discounts, code) =>
  discounts?.find((d) => d.code === code) || null;

export const checkDiscountAppliedValidWhenPaymentMethodChangeAfterTxnCreated =
  ({ isUpdateTxn, discount, currentPaymentMethodSelected }) =>
    // Boolean(
    //   isUpdateTxn &&
    //     discount &&
    //     (!discount?.paymentMethod ||
    //       discount?.paymentMethod === currentPaymentMethodSelected)
    // );
    Boolean(isUpdateTxn && discount);

export const checkDiscountAppliedInvalidWhenPaymentMethodChangeBeforeTxnCreated =
  ({
    isUpdateTxn,
    discountApplied,
    prevPaymentMethodSelected,
    currentPaymentMethodSelected,
  }) =>
    Boolean(
      !isUpdateTxn &&
      discountApplied &&
      prevPaymentMethodSelected !== currentPaymentMethodSelected,
    );

export const getDiscountsActiveList = ({
  discountsAvailable,
  discountsInactive,
}) => [
  ...(isArray(discountsAvailable) ? discountsAvailable : []),
  ...(isArray(discountsInactive) ? discountsInactive : []),
];
export const getDiscountDefault = ({
  discountsAvailable,
  discountsInactive,
}) => {
  const discountsActive = getDiscountsActiveList({
    discountsAvailable,
    discountsInactive,
  });
  return !isEmpty(discountsActive) && isArray(discountsActive)
    ? discountsActive?.find((discount) => discount?.isDefault)
    : null;
};

export const handleOnCloseFxPaymentMethodOnChangePopup = ({ callback }) => {
  callback();
};
export const handleOnAgreeFxPaymentMethodOnChangePopup = (payload) => {
  const { paymentMethod, callback } = payload || {};

  const statePayload = {
    ...stateStore.addTransactionInformation,
    paymentMethod: {
      ...stateStore.addTransactionInformation.paymentMethod,
      ...(paymentMethod && {
        [ADD_TRANSACTION_PAYMENT_METHOD_NAME]: paymentMethod,
      }),
      ...(Boolean(callback) && {
        discountsAvailable: [],
        discountsInactive: [],
      }),
      discountApplied: null,
    },
  };
  dispatchStore({
    type: SET_ADD_TRANSACTION_INFORMATION,
    payload: statePayload,
  });

  callback && callback();
};
export const renderFxPaymentMethodOnChangePopup = ({
  t,
  isOpen,
  setOpen,
  paymentMethod,
  onCloseExt,
  onClickExt,
  contentReplace,
  isDesktop,
}) => (
  <InformPopup
    isOpen={isOpen}
    styles={{
      width: isDesktop ? '436px' : '100%',
    }}
    imageSrc={NoticeIcon}
    imagesStyles={{
      width: '75px',
      height: '60.5px',
      marginBottom: '16px',
    }}
    text={''}
    content={contentReplace || t('discount_important_desc')}
    buttonLabel1={t('button_no')}
    onClose={() =>
      handleOnCloseFxPaymentMethodOnChangePopup({
        callback: () => {
          setOpen(false);
          onCloseExt && onCloseExt();
        },
      })
    }
    buttonLabel2={t('button_yes')}
    onClick={() => {
      handleOnAgreeFxPaymentMethodOnChangePopup({
        paymentMethod,
        callback: () => {
          setOpen(false);
          onClickExt && onClickExt();
        },
      });
    }}
    isBackdropOverlap={true}
    zIndexBackdropOverlap={1055}
    isClickBackdropToClose={false}
  />
);
